import { useProfessionalCreditStatus } from '@common/credits/ProfessionalEdition/hooks';
import { PLAN_TYPES } from '@common/credits/ProfessionalEdition/types';
import { useGitStatus } from '@common/ExternalGitStatus/useExternalGitStatus';
import { OrchestrationConnection } from '@common/Fabric/Connections/types';
import { useOrchestrationFabricConnections } from '@common/Fabric/Connections/useOrchestrationFabricConnections';
import { FabricIcons } from '@common/Fabric/icons';
import { FabricProviderType } from '@common/Fabric/types';
import { ORCHESTRATION_PROVIDER } from '@common/Fabric/types/orchestration';
import {
  getFabricProviderInfo,
  isAirflowFabricProvider,
  isOrchestrationFabricProvider,
  isSparkFabricProvider,
  isSQLFabricProvider
} from '@common/Fabric/utils';
import { LanguageIcon } from '@common/language';
import { useUserFabricsWithoutAnalystFabric } from '@common/onboarding/hooks';
import { Entity } from '@common/types/Entity';
import { CallBackEntityParams, Private_Routes, VAPP_Private_Routes } from '@common/url';
import { titleCase } from '@common/utils/metadataUtils';
import { useUserWorkspace } from '@common/VerticalApps/hooks';
import { useIsVerticalApp } from '@common/VerticalApps/utils';
import { useTranspilerEnabledLanguages } from '@pages/Transpiler/hooks';
import { useBranchData } from '@pages/Transpiler/useBranchData';
import { useProjectTeamsQuery } from '@pages/Transpiler/useProjectTeamsQuery';
import { BackEndCodeLanguage, BackEndCodeLanguageType } from '@prophecy/graphql-types/enums';
import { BaseColor, theme } from '@prophecy/theme';
import { AutoComplete, Badges, Checkbox, Ellipsis, GhostInput, Select, Stack, StackItem, Text } from '@prophecy/ui-v3';
import { FROZEN_EMPTY_ARRAY } from '@prophecy/utils/constants';
import { useOnMount } from '@prophecy/utils/react/hooks';
import { isCaseInsensitiveEqual } from '@prophecy/utils/string';
import { validateEntityName } from '@prophecy/utils/validation';
import { SQL_PROVIDER } from 'frontend/core/src/constants/sql';
import { DefaultOptionType } from 'rc-select/lib/Select';
import React, { useEffect, useId, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import styled from 'styled-components';

import { useGlobalVariables } from '../../../../context/globalConfig/securedConfigHook';
import { ProjectTypes } from '../../../CreateEntity/Project/common';
import { ProjectType } from '../../../CreateEntity/Project/ProjectType';
import { PoolDetail } from '../../../Workflow/AttachFabric/ClusterDetail';
import { FileFormatLabels, FileFormats } from '../../config';
import { useImportState } from '../useImportState';
import { isInformatica as _isInformatica } from '../utils';
import { StyledHeadingText, StyledStackContainer, StyledWhiteCard } from './styled';
import { ImportStepValue } from './types';
import { filterFabricsByProviderType, SelectLinkFooter } from './utils';

// Only DataBricks, BigQuery, Snowflake and ProphecyManaged support orchestration pipelines;
// Trino, and Redshift do not support orch/pipelines.
const ORCHESTRATION_PROVIDERS: string[] = [
  SQL_PROVIDER.DataBricks,
  SQL_PROVIDER.BigQuery,
  SQL_PROVIDER.Snowflake,
  ORCHESTRATION_PROVIDER.ProphecyManaged
];

const StyledGhostInput = styled(GhostInput)`
  &&& input {
    min-width: 420px;
  }
`;

const OptionLabel = ({ name, text, icon }: { name: string; text?: string; icon?: React.ReactNode }) => {
  return (
    <Stack direction='horizontal' width='100%' gap={theme.spaces.x8} align='space-between' alignY='center'>
      <Stack shrink='1' direction='horizontal' gap={theme.spaces.x8} alignY='center'>
        {icon}
        <Ellipsis tooltip={true}>
          <Text level='sm' tone={theme.colors.gray700}>
            {name}
          </Text>
        </Ellipsis>
      </Stack>
      {text && (
        <Text level='sm13' tone={theme.colors.gray500}>
          {text}
        </Text>
      )}
    </Stack>
  );
};

export const BasicOptions = () => {
  const { plan, teamID: workspaceId } = useProfessionalCreditStatus();
  const isProfessionalEdition = plan === PLAN_TYPES.Pro;
  const {
    formDataWithActions: { formContext },
    transpilerState: { transpilerId }
  } = useImportState();
  const isJobTranspileLabelId = useId();
  const convertLookupsLabelId = useId();
  const {
    control,
    getValues,
    setValue,
    watch,
    formState: { errors },
    trigger
  } = formContext;
  const globalConf = useGlobalVariables();
  const source = watch('source');
  const isAlteryx = source === FileFormats.alteryx;

  const enabledLanguages = useTranspilerEnabledLanguages(source as FileFormats);

  const formData = getValues();
  const isAdvance = globalConf.isAdvanceTranspilerEnabled;
  const isJobEnabled =
    globalConf.isTranspilerJobEnabled && (source === FileFormats.abinitio || source === FileFormats.datastage);
  const { data, isLoading, isError } = useProjectTeamsQuery();
  const teamId = watch('teamId') as string;
  const language = watch('language');
  const showOnlyOrchProjects = language === BackEndCodeLanguage.sql && isAlteryx;
  const { teamOptions, projectOptions } = useMemo(() => {
    const teams = data?.User?.teams || [];
    const teamOptions = teams.map((team) => ({ label: team.name, value: team._id }));
    const team = teams.find((team) => team._id === teamId);
    const pythonOption: DefaultOptionType = {
      label: `Language: ${titleCase(BackEndCodeLanguage.python)}`,
      value: BackEndCodeLanguage.python,
      options: [] as DefaultOptionType[]
    };

    const scalaOption: DefaultOptionType = {
      label: `Language: ${titleCase(BackEndCodeLanguage.scala)}`,
      value: BackEndCodeLanguage.scala,
      options: [] as DefaultOptionType[]
    };
    const sqlOption: DefaultOptionType = {
      label: `Language: ${titleCase(BackEndCodeLanguage.sql)}`,
      value: BackEndCodeLanguage.sql,
      options: [] as DefaultOptionType[]
    };
    team?.projects.forEach((project) => {
      const isSelected = project.language === language;
      const isOrchestrationProvider = ORCHESTRATION_PROVIDERS.some((provider) =>
        isCaseInsensitiveEqual(project.providerType, provider)
      );
      const notSupported = showOnlyOrchProjects && !isOrchestrationProvider;
      let labelText = '';

      if (!isSelected) {
        labelText = 'Read-only';
      } else if (notSupported) {
        labelText = 'Not supported';
      }

      const projectOption = {
        value: project._id,
        key: project.name,
        language: project.language,
        disabled: !isSelected || notSupported,
        label: (
          <OptionLabel
            name={project.name}
            text={labelText}
            icon={<LanguageIcon language={project.language} size={'s'} />}
          />
        )
      };
      if (project.language === BackEndCodeLanguage.python) {
        pythonOption.options.push(projectOption);
      } else if (project.language === BackEndCodeLanguage.scala) {
        scalaOption.options.push(projectOption);
      } else if (project.language === BackEndCodeLanguage.sql) {
        sqlOption.options.push(projectOption);
      }
    });
    let options: DefaultOptionType[] = [];
    if (scalaOption.options.length > 0) {
      options.push(scalaOption);
    }
    if (sqlOption.options.length > 0) {
      options = language === BackEndCodeLanguage.sql ? [sqlOption, ...options] : [...options, sqlOption];
    }
    if (pythonOption.options.length > 0) {
      // if language is python keep the python option as first option or else keep it last.
      options = language === BackEndCodeLanguage.python ? [pythonOption, ...options] : [...options, pythonOption];
    }
    return {
      teamOptions,
      projectOptions: options
    };
  }, [showOnlyOrchProjects, language, data?.User?.teams, teamId]);

  const projectId = watch('projectId');
  const fabrics = useUserFabricsWithoutAnalystFabric();

  const isSQlLanguage = language === BackEndCodeLanguage.sql;

  const selectedProject = useMemo(() => {
    const teams = data?.User?.teams || [];
    const team = teams.find((t) => t._id === teamId);
    return team?.projects?.find((p) => p._id === projectId);
  }, [data?.User?.teams, teamId, projectId]);

  const { airflowFabrics, sqlFabrics, sparkFabrics } = useMemo(() => {
    const sparkFabrics: DefaultOptionType[] = [];
    const sqlFabrics: DefaultOptionType[] = [];
    const airflowFabrics: DefaultOptionType[] = [];
    fabrics.forEach((fabric) => {
      const providerInfo = getFabricProviderInfo(fabric);
      if (!providerInfo) {
        return;
      }

      const isOrchestrationType = isOrchestrationFabricProvider(providerInfo);
      if (isSparkFabricProvider(providerInfo)) {
        const icon = FabricIcons.Spark[providerInfo.provider];
        sparkFabrics.push({
          label: <OptionLabel icon={icon} name={fabric.name} />,
          value: fabric.id,
          name: fabric.name
        });
      }
      if (isSQLFabricProvider(providerInfo) || isOrchestrationType) {
        const icon = isOrchestrationType
          ? FabricIcons[FabricProviderType.Orchestration][providerInfo.provider as ORCHESTRATION_PROVIDER]
          : FabricIcons.SQL[providerInfo.provider as SQL_PROVIDER];
        sqlFabrics.push({
          label: <OptionLabel icon={icon} name={fabric.name} />,
          value: fabric.id,
          name: fabric.name,
          isOrchestrationType
        });
      }
      if (isAirflowFabricProvider(providerInfo)) {
        const icon = FabricIcons.Airflow[providerInfo.provider];
        airflowFabrics.push({
          label: <OptionLabel icon={icon} name={fabric.name} />,
          name: fabric.name,
          value: fabric.id
        });
      }
    });
    return { sparkFabrics, airflowFabrics, sqlFabrics };
  }, [fabrics]);

  const { isSimpleVersionControl } = useGitStatus(projectId);

  const showBranch = isSimpleVersionControl === false;

  const { isProjectBranchLoading, branches, currentMainBranch, workingBranch } = useBranchData(projectId);

  const currentBranch = formData.branch;

  const urlParams = {
    ...formData,
    entity: Entity.Transpiler,
    entityId: transpilerId,
    step: `${ImportStepValue.PackageDetail}`
  } as CallBackEntityParams;

  useEffect(() => {
    if (!currentBranch && workingBranch !== currentMainBranch) {
      setValue('branch', (workingBranch !== currentMainBranch ? workingBranch : '') || '', { shouldValidate: true });
    }
    // we don't want to listen for currentBranch changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workingBranch, currentMainBranch, setValue]);

  useEffect(() => {
    if (!showOnlyOrchProjects || !projectId || !teamId) return;

    const teams = data?.User?.teams || [];
    const team = teams.find((t) => t._id === teamId);
    const project = team?.projects?.find((p) => p._id === projectId);
    if (!project) return;

    const isOrchestrationProvider = ORCHESTRATION_PROVIDERS.some((provider) =>
      isCaseInsensitiveEqual(project.providerType, provider)
    );
    if (!isOrchestrationProvider) {
      setValue('projectId', '');
      setValue('branch', '');
      setValue('fabricId', '', { shouldValidate: true });
    }
  }, [isAlteryx, showOnlyOrchProjects, projectId, teamId, data?.User?.teams, setValue]);

  useOnMount(() => {
    trigger('branch');
  });

  useEffect(() => {
    if (isProfessionalEdition && workspaceId) {
      setValue('teamId', workspaceId);
    }
  }, [isProfessionalEdition, workspaceId, setValue]);

  // Setting one empty option to make Create new footer visible in select box
  const defaultOptions = useMemo(() => [{ label: 'No data available', value: '', disabled: true }], []);
  const isJobTranspile = watch('isJobTranspile') && isJobEnabled;
  const fabricId = watch('fabricId');
  const isVerticalApp = useIsVerticalApp();
  const workspaceFabricId = useUserWorkspace().currentFabric?.fabric.id;
  // Execution Environment is hidden in vertical apps; keep the workspace fabric applied even after other fields reset it.
  useEffect(() => {
    if (isVerticalApp && workspaceFabricId && fabricId !== workspaceFabricId) {
      setValue('fabricId', workspaceFabricId, { shouldValidate: true });
    }
  }, [isVerticalApp, workspaceFabricId, fabricId, setValue]);
  const pools = useMemo(() => {
    return (fabrics.find((fabric) => fabric.id === fabricId)?.JobSize?.pools || []).map((pool) => ({
      label: <PoolDetail pool={pool} />,
      value: pool.name
    }));
  }, [fabrics, fabricId]);

  const isInformatica = _isInformatica(source as FileFormats);
  const hasSql = isAlteryx || isInformatica;

  const executionEnvOptions = useMemo(() => {
    const envs = isSQlLanguage
      ? sqlFabrics.length > 0
        ? sqlFabrics
        : defaultOptions
      : sparkFabrics.length > 0
        ? sparkFabrics
        : defaultOptions;
    if (envs === defaultOptions) {
      return envs;
    }
    return filterFabricsByProviderType(envs, fabrics, isSQlLanguage, selectedProject?.providerType);
  }, [isSQlLanguage, sqlFabrics, sparkFabrics, selectedProject, defaultOptions, fabrics]);

  const isOrchestrationFabricSelected = hasSql
    ? !!sqlFabrics.find((fabric) => fabric.value === fabricId && fabric.isOrchestrationType)
    : false;

  const {
    connections = FROZEN_EMPTY_ARRAY as OrchestrationConnection[],
    isLoading: isConnectionLoading,
    isRefetching: isConnectionRefetching
  } = useOrchestrationFabricConnections();

  const orchestrationConnections: DefaultOptionType[] = useMemo(
    () =>
      connections.map((connection) => {
        if (isOrchestrationFabricSelected && !getValues('connectionId') && connection.isDefaultWarehouseConnection) {
          setValue('connectionId', connection.id);
        }
        return {
          label: connection.name,
          value: connection.id
        };
      }),
    [connections, isOrchestrationFabricSelected, getValues, setValue]
  );

  return (
    <StyledWhiteCard>
      <Stack grow='1' width='100%' gap={theme.spaces.x16} padding={theme.spaces.x4}>
        <Text level='xl' weight={theme.fontWeight.medium} tone={theme.colors.gray900}>
          2. Bundle Details
        </Text>
        <Text level='sm' tone={theme.colors.gray700}>
          Choose the distinctive name for the {FileFormatLabels[source as FileFormats]} bundle to help in future
          identification and fixes.
        </Text>
        <StackItem grow='1'>
          <StyledStackContainer gap={theme.spaces.x16} grow='1' padding={`${theme.spaces.x20} ${theme.spaces.x6}`}>
            <Controller
              render={({ field }) => (
                <StyledGhostInput
                  inputSize='l'
                  placeholder='Import Bundle Name'
                  {...field}
                  variant={errors.name ? 'error' : 'primary'}
                  helpText={errors.name ? errors.name?.message : ``}
                  data-test-id='transpiler-basic-options-import-bundle-name-input'
                />
              )}
              control={control}
              name='name'
              rules={{
                required: 'Name is required',
                validate: validateEntityName
              }}
            />
            <StyledHeadingText level='md' weight={theme.fontWeight.medium} tone={theme.colors.gray900}>
              Target Code Repository
            </StyledHeadingText>
            <Controller
              render={({ field }) => (
                <ProjectType
                  title='Language'
                  {...field}
                  required
                  onChange={(value) => {
                    if (value !== getValues('language')) {
                      field.onChange(value);
                      setValue('projectId', '');
                      setValue('fabricId', '');
                      setValue('airflowFabricId', '');
                      setValue('branch', '');
                    }
                  }}
                  sqlLabel={
                    !hasSql ? (
                      <Badges size='s' variant='pillDark' disabled={true} tone={BaseColor.primary}>
                        Coming soon..
                      </Badges>
                    ) : undefined
                  }
                  disableSql={!hasSql}
                  size='sm13'
                  label={ProjectTypes[field.value as BackEndCodeLanguageType].label}
                  value={field.value as BackEndCodeLanguage}
                  disabled={!formData.isLanguageEnabled}
                  enabledLanguages={enabledLanguages}
                />
              )}
              control={control}
              name='language'
            />
            {!isProfessionalEdition && (
              <Controller
                render={({ field }) => (
                  <Select
                    label='Team'
                    {...field}
                    required
                    showSearch={true}
                    disabled={isLoading || isError}
                    data-test-id='transpiler-import-team-select'
                    options={teamOptions}
                    onChange={(value) => {
                      field.onChange(value);
                      setValue('projectId', '');
                      setValue('fabricId', '', { shouldValidate: true });
                    }}
                    value={field.value || 'Select or Create Team'}
                    variant={errors.teamId ? 'error' : 'primary'}
                    footer={<SelectLinkFooter to={Private_Routes.Create_Entity.team.getUrl(undefined, urlParams)} />}
                    helpText={
                      errors.teamId ? (
                        errors.teamId?.message
                      ) : (
                        <Text level='xs' tone={theme.colors.gray500}>
                          Selected Team will be given access to the bundle.
                        </Text>
                      )
                    }
                  />
                )}
                rules={{
                  required: 'Team is required'
                }}
                control={control}
                name='teamId'
              />
            )}
            <Controller
              render={({ field }) => (
                <Select
                  label='Project'
                  {...field}
                  required
                  filterOption={true}
                  optionFilterProp='key'
                  showSearch={true}
                  onChange={(value) => {
                    field.onChange(value);
                    setValue('branch', '');
                  }}
                  data-test-id='transpiler-basic-options-project-select'
                  disabled={isLoading || isError}
                  options={projectOptions.length > 0 ? projectOptions : defaultOptions}
                  variant={errors.projectId ? 'error' : 'primary'}
                  value={field.value || 'Select or Create Project'}
                  footer={
                    <SelectLinkFooter
                      to={
                        isProfessionalEdition
                          ? VAPP_Private_Routes.Project_Create.getUrl(undefined)
                          : Private_Routes.Create_Entity.project.getUrl(undefined, { ...urlParams, language, teamId })
                      }
                    />
                  }
                  helpText={
                    errors.projectId ? (
                      errors.projectId?.message
                    ) : (
                      <Text level='xs' tone={theme.colors.gray500}>
                        Select Project (git repository), where Prophecy pipelines will be uploaded.
                      </Text>
                    )
                  }
                />
              )}
              rules={{
                required: 'Project is required'
              }}
              control={control}
              name='projectId'
            />
            {showBranch && projectId && (
              <Controller
                render={({ field }) => (
                  <AutoComplete
                    label='Branch'
                    {...field}
                    required
                    disabled={isProjectBranchLoading || isLoading || isError}
                    loading={isProjectBranchLoading}
                    variant={errors.branch ? 'error' : 'primary'}
                    placeholder='Find or create a branch'
                    options={branches}
                    helpText={
                      errors.branch ? (
                        errors.branch?.message
                      ) : (
                        <Text level='xs' tone={theme.colors.gray500}>
                          Select Git Branch, where Prophecy pipelines will be uploaded.
                        </Text>
                      )
                    }
                    data-test-id='transpiler-basic-options-branch-select'
                  />
                )}
                rules={{
                  required: 'Branch name is required',
                  validate: (_branchName) => {
                    const branchName = _branchName.trim();
                    const selectedBranch = branches.find((branch) => branchName === branch.label);
                    if (selectedBranch?.disabled) {
                      return `Updating ${branchName} is not allowed. Please select another branch`;
                    }
                  }
                }}
                control={control}
                name='branch'
              />
            )}
            {!isVerticalApp && (
              <>
                <StyledHeadingText level='md' weight={theme.fontWeight.medium} tone={theme.colors.gray900}>
                  Execution Environment
                </StyledHeadingText>
                <Controller
                  render={({ field }) => (
                    <Select
                      label={isSQlLanguage ? 'Sql Fabric' : 'Spark Fabric'}
                      {...field}
                      filterOption={true}
                      optionFilterProp='name'
                      required
                      showSearch={true}
                      options={executionEnvOptions}
                      disabled={isLoading || isError}
                      value={field.value || (isProfessionalEdition ? 'Select Fabric' : 'Select or Create Fabric')}
                      variant={errors.fabricId ? 'error' : 'primary'}
                      onChange={(value) => {
                        field.onChange(value);
                        setValue('clusterSize', '');
                        setValue('connectionId', '');
                      }}
                      footer={
                        isProfessionalEdition ? undefined : (
                          <SelectLinkFooter
                            to={Private_Routes.Create_Entity.fabric.new.getUrl(undefined, {
                              teamId,
                              ...urlParams,
                              fabricProviderType: isSQlLanguage ? FabricProviderType.SQL : FabricProviderType.Spark
                            })}
                          />
                        )
                      }
                      helpText={
                        errors.fabricId ? (
                          errors.fabricId?.message
                        ) : (
                          <Text level='xs' tone={theme.colors.gray500}>
                            Prophecy Fabric, to which the datasets and schedules are going to be attached to.
                          </Text>
                        )
                      }
                      data-test-id='transpiler-basic-options-fabric-select'
                    />
                  )}
                  rules={{
                    required: `${isSQlLanguage ? 'Sql' : 'Spark'} Fabric is required`
                  }}
                  control={control}
                  name='fabricId'
                />
                {!isProfessionalEdition && isOrchestrationFabricSelected && fabricId && (
                  <Controller
                    render={({ field }) => (
                      <Select
                        label='Fabric Connection'
                        {...field}
                        loading={isConnectionLoading || isConnectionRefetching}
                        showSearch={true}
                        options={orchestrationConnections.length > 0 ? orchestrationConnections : defaultOptions}
                        disabled={isError}
                        value={field.value || 'Select or Create connection'}
                        footer={
                          <SelectLinkFooter
                            to={Private_Routes.Fabric.tab.getUrl({ uid: fabricId, tab: 'connections' })}
                          />
                        }
                        data-test-id='transpiler-basic-options-fabric-connection-select'
                      />
                    )}
                    control={control}
                    name='connectionId'
                  />
                )}
                {isJobEnabled && !isSQlLanguage && (
                  <Controller
                    render={({ field }) => (
                      <Stack direction='horizontal' gap={theme.spaces.x6} alignY='center'>
                        <Checkbox
                          {...field}
                          value={`${field.value}`}
                          checked={Boolean(field.value)}
                          onChange={(checked) => {
                            if (!checked) {
                              setValue('clusterSize', undefined);
                              setValue('airflowFabricId', undefined);
                            }
                            setValue('isJobTranspile', checked, {
                              shouldValidate: true
                            });
                          }}
                          disabled={isLoading || isError}
                          aria-labelledby={isJobTranspileLabelId}
                        />
                        <span id={isJobTranspileLabelId}>
                          {source === FileFormats.abinitio
                            ? `Do you want to transpile Ab Initio plans?`
                            : `Do you want to transpile Datastage sequence jobs?`}
                        </span>
                      </Stack>
                    )}
                    control={control}
                    name='isJobTranspile'
                  />
                )}
                {isJobTranspile && (
                  <>
                    <Controller
                      render={({ field }) => (
                        <Select
                          label='Databricks Cluster Size'
                          {...field}
                          showSearch={true}
                          options={pools || []}
                          disabled={isLoading || isError || !fabricId}
                          value={field.value || 'Cluster Size'}
                          helpText={
                            errors.clusterSize ? (
                              errors.clusterSize?.message
                            ) : (
                              <Text level='xs' tone={theme.colors.gray500}>
                                Select Cluster size for Databricks Fabric.
                              </Text>
                            )
                          }
                          data-test-id='transpiler-basic-options-cluster-size-select'
                        />
                      )}
                      control={control}
                      name='clusterSize'
                      rules={{
                        validate: (value) => (isJobTranspile && !value ? 'Cluster size is required' : undefined)
                      }}
                    />
                    <Controller
                      render={({ field }) => (
                        <Select
                          label='Airflow Fabric'
                          {...field}
                          filterOption={true}
                          optionFilterProp='name'
                          showSearch={true}
                          options={airflowFabrics.length > 0 ? airflowFabrics : defaultOptions}
                          disabled={isLoading || isError}
                          value={field.value || (isProfessionalEdition ? 'Select Fabric' : 'Select or Create Fabric')}
                          footer={
                            isProfessionalEdition ? undefined : (
                              <SelectLinkFooter
                                to={Private_Routes.Create_Entity.fabric.new.getUrl(undefined, {
                                  teamId,
                                  ...urlParams,
                                  fabricProviderType: FabricProviderType.Airflow
                                })}
                              />
                            )
                          }
                          helpText={
                            errors.airflowFabricId ? (
                              errors.airflowFabricId?.message
                            ) : (
                              <Text level='xs' tone={theme.colors.gray500}>
                                Please make sure your Airflow Fabric is connected to databricks fabric for seamless Job
                                Execution.
                              </Text>
                            )
                          }
                          data-test-id='transpiler-basic-options-airflow-fabric-select'
                        />
                      )}
                      rules={{
                        validate: (value) => (isJobTranspile && !value ? 'Airflow Fabric is required' : undefined)
                      }}
                      control={control}
                      name='airflowFabricId'
                    />
                  </>
                )}
              </>
            )}

            {isAdvance && (
              <>
                <StyledHeadingText level='md' weight={theme.fontWeight.medium} tone={theme.colors.gray900}>
                  Advanced
                </StyledHeadingText>
                <Controller
                  render={({ field }) => (
                    <Stack direction='horizontal' gap={theme.spaces.x12} alignY='start'>
                      <Checkbox
                        {...field}
                        value={`${field.value}`}
                        checked={Boolean(field.value)}
                        disabled={isLoading || isError}
                        aria-labelledby={convertLookupsLabelId}
                      />
                      <Stack gap={theme.spaces.x2} id={convertLookupsLabelId}>
                        <Text level='sm13' tone={theme.colors.gray700}>
                          Convert all Lookups components to Joins
                        </Text>
                        <Text level='xs' tone={theme.colors.gray500}>
                          Prophecy lookup gems are best suited for less than 5MB of data. For larger tables it's
                          recommended to use Join's.
                        </Text>
                      </Stack>
                    </Stack>
                  )}
                  control={control}
                  name='convertLookupsToJoin'
                />
              </>
            )}
          </StyledStackContainer>
        </StackItem>
      </Stack>
    </StyledWhiteCard>
  );
};
